import { SearchIcon, SettingsIcon, StarIcon } from '@chakra-ui/icons';
import {
  Box,
  Icon as ChakraIcon,
  Flex,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import type { ComponentProps } from 'react';

export type ImpersonationMode = 'preset' | 'search' | 'custom';

interface ModeOption {
  mode: ImpersonationMode;
  title: string;
  description: string;
  icon: ComponentProps<typeof ChakraIcon>['as'];
}

const MODE_OPTIONS: ModeOption[] = [
  {
    mode: 'preset',
    title: 'Use a preset',
    description: 'Quickly apply a CST or Free Trial setup',
    icon: StarIcon,
  },
  {
    mode: 'search',
    title: 'Search a user',
    description: 'Impersonate an existing client user',
    icon: SearchIcon,
  },
  {
    mode: 'custom',
    title: 'Custom overrides',
    description: 'Manually configure tabs, role, datasets…',
    icon: SettingsIcon,
  },
];

interface ImpersonationModePickerProps {
  mode: ImpersonationMode;
  onChange: (mode: ImpersonationMode) => void;
}

export function ImpersonationModePicker({
  mode,
  onChange,
}: ImpersonationModePickerProps) {
  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
      {MODE_OPTIONS.map((option) => {
        const isActive = mode === option.mode;
        return (
          <Flex
            key={option.mode}
            as="button"
            type="button"
            align="flex-start"
            gap={3}
            p={4}
            border="1px solid"
            borderColor={isActive ? 'blue.500' : 'gray.200'}
            borderRadius="lg"
            bg={isActive ? 'blue.50' : 'white'}
            boxShadow={
              isActive ? '0 0 0 1px var(--chakra-colors-blue-500)' : 'none'
            }
            textAlign="left"
            cursor="pointer"
            transition="all 0.12s"
            _hover={{ borderColor: 'blue.400' }}
            onClick={() => onChange(option.mode)}
            data-testid={`impersonation-mode-${option.mode}`}
            aria-pressed={isActive}
          >
            <Flex
              w="36px"
              h="36px"
              borderRadius="md"
              align="center"
              justify="center"
              bg={isActive ? 'blue.100' : 'gray.100'}
              color={isActive ? 'blue.600' : 'gray.600'}
              flexShrink={0}
            >
              <ChakraIcon as={option.icon} boxSize={4} />
            </Flex>
            <Box minW={0}>
              <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                {option.title}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={0.5}>
                {option.description}
              </Text>
            </Box>
          </Flex>
        );
      })}
    </SimpleGrid>
  );
}
